// pdfDataCache.js
// Implements a cache for PDF document data like lookups and tab contents

class PDFDataCache {
  constructor(limit = 100, ttl = 600000) {
    // Default TTL: 10 minutes (600000ms)
    this.limit = limit;
    this.cache = new Map();
    this.ttlMap = new Map(); // Map to store expiry timestamps
    this.hits = 0;
    this.misses = 0;
    this.errors = 0;
    this.ttl = ttl;
  }

  /**
   * Get data for document/tab from cache
   * @param {string} documentId - Document ID
   * @param {string} tab - Tab or lookup type
   * @param {string} [extraKey] - Optional additional key parameter
   * @returns {any|null} - Cached data or null if not found/expired
   */
  get(documentId, tab, extraKey = '') {
    const key = this.generateKey(documentId, tab, extraKey);
    const value = this.cache.get(key);
    const expiry = this.ttlMap.get(key);

    if (value && expiry && expiry > Date.now()) {
      this.hits++;
      // Move to end to mark as recently used (LRU behavior)
      this.cache.delete(key);
      this.ttlMap.delete(key);
      this.cache.set(key, value);
      this.ttlMap.set(key, expiry);
      return value;
    }

    // If expired, remove it
    if (expiry && expiry <= Date.now()) {
      this.cache.delete(key);
      this.ttlMap.delete(key);
    }

    this.misses++;
    return null;
  }

  /**
   * Store data in cache
   * @param {string} documentId - Document ID
   * @param {string} tab - Tab or lookup type
   * @param {any} value - Data to be cached
   * @param {string} [extraKey] - Optional additional key parameter
   * @param {number} [customTtl] - Optional custom TTL in milliseconds
   */
  set(documentId, tab, value, extraKey = '', customTtl) {
    try {
      // Skip caching if value is invalid
      if (!value) {
        this.errors++;
        return;
      }

      const key = this.generateKey(documentId, tab, extraKey);

      // Remove oldest if at limit
      if (this.cache.size >= this.limit) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
        this.ttlMap.delete(firstKey);
      }

      // Set expiration time
      const expiry = Date.now() + (customTtl || this.ttl);

      this.cache.set(key, value);
      this.ttlMap.set(key, expiry);
    } catch (error) {
      console.warn('Error caching PDF data:', error);
      this.errors++;
    }
  }

  /**
   * Generate a consistent cache key
   * @private
   */
  generateKey(documentId, tab, extraKey) {
    return `${documentId}_${tab}_${extraKey}`;
  }

  /**
   * Clear all cached data for a specific document
   * @param {string} documentId - Document ID to clear
   */
  clearDocument(documentId) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${documentId}_`)) {
        this.cache.delete(key);
        this.ttlMap.delete(key);
      }
    }
  }

  /**
   * Clear specific tab data for a document
   * @param {string} documentId - Document ID
   * @param {string} tab - Tab type to clear
   */
  clearTab(documentId, tab) {
    const prefix = `${documentId}_${tab}_`;
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
        this.ttlMap.delete(key);
      }
    }
  }

  /**
   * Clear entire cache
   */
  clear() {
    this.cache.clear();
    this.ttlMap.clear();
    this.hits = 0;
    this.misses = 0;
    this.errors = 0;
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const now = Date.now();
    for (const [key, expiry] of this.ttlMap.entries()) {
      if (expiry <= now) {
        this.cache.delete(key);
        this.ttlMap.delete(key);
      }
    }
  }

  /**
   * Handle memory pressure by clearing oldest entries
   * @param {number} percentage - Percentage of cache to clear (0-100)
   */
  handleMemoryPressure(percentage = 50) {
    const itemsToRemove = Math.floor(this.cache.size * (percentage / 100));
    let removed = 0;

    for (const key of this.cache.keys()) {
      if (removed >= itemsToRemove) break;
      this.cache.delete(key);
      this.ttlMap.delete(key);
      removed++;
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      hits: this.hits,
      misses: this.misses,
      errors: this.errors,
      hitRate: this.hits / (this.hits + this.misses) || 0,
    };
  }
}

// Export a singleton instance with increased limit
export default new PDFDataCache(100); // Increased from 50 to 100 for better performance
